import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { i18n } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { retryParseParsingCase } from './retryParseParsingCase';
import { type RetryParseParsingCasePayload } from './types';

/**
 * Custom hook to retry the parse step of a parsing case from the source RAW blob.
 * The mutation accepts an optional body that overrides site/accounting reference or
 * supplies a corrected OCR payload (skipping the AI OCR call).
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_parse_vadmin_parsing_cases__case_id__actions_retry_parse_post
 */
export const useMutationRetryParseParsingCase = (
  options?: UseMutationOptions<
    ParsingCaseActionMutationResponse,
    unknown,
    RetryParseParsingCasePayload
  >,
) => {
  const actionOptions =
    useParsingCaseActionMutationOptions<RetryParseParsingCasePayload>(() =>
      i18n.t(($) => $.retryParseParsingCase.success, { ns: 'data' }),
    );

  return useMutation<
    ParsingCaseActionMutationResponse,
    unknown,
    RetryParseParsingCasePayload
  >({
    mutationFn: async (payload) => retryParseParsingCase(payload),
    ...actionOptions,
    ...options,
  });
};
